import { beforeSendScrubSamlp } from '@remarkable/sentry-utils/common';
// Import the generated route tree
import * as Sentry from '@sentry/react';
import loglevel from 'loglevel';
import ReactDOM from 'react-dom/client';
import { analyticsHelpers } from './analytics/utils';
import { App } from './app';
import { CONFIG, IS_DEVELOPMENT } from './config';
import './scss/app.scss';
import './scss/tailwind.css';
import { router } from './router';
import { logger } from './utils/logger';
import { categoryLevel } from './utils/logger.categories';
import { getLogLevelBySentrySeverityLevel } from './utils/logger.sentry';

// Track landing page (first page view only)
analyticsHelpers.trackPageview(window.location.pathname);

router.history.subscribe((opts) => {
  let path = opts.location.pathname;

  if (opts.location.search.includes('showOtp')) {
    path += opts.location.search;
  }
  analyticsHelpers.trackPageview(path, opts.action.type);
});

try {
  Sentry.init({
    dsn: 'https://56f51ac528f948519fea1f531da7211a@o100763.ingest.sentry.io/1418691',
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
      Sentry.breadcrumbsIntegration({
        dom: {
          serializeAttribute: ['data-cy', 'href', 'id', 'name'],
        },
      }),
      Sentry.extraErrorDataIntegration({ depth: 5 }),
    ],
    environment: CONFIG.Environment ?? 'development',
    release: CONFIG.Release,
    // Session Replay
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.1,
    tracesSampleRate: 0.03,
    beforeSend: (event) => beforeSendScrubSamlp(event),
    beforeBreadcrumb(breadcrumb) {
      const { category, message, timestamp, level, ...rest } = breadcrumb;

      if (category === 'console') {
        return breadcrumb;
      }

      const finalLevel =
        getLogLevelBySentrySeverityLevel(level) ||
        categoryLevel[category as keyof typeof categoryLevel] ||
        loglevel.levels.TRACE;

      const data = Object.keys(rest).length > 0 ? rest : undefined;

      logger.log({
        category,
        message,
        level: finalLevel,
        timestamp,
        data,
      });

      return breadcrumb;
    },
    normalizeDepth: 5,
  });
} catch (e) {
  console.error('Sentry init failed', e);
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// A dynamically imported route chunk or modules can fail to load, e.g. because a new deployment
// removed the old chunk, or the fetch was interrupted by a network blip. Log it with its
// own tag for now so we can see the real volume/pattern before deciding whether to
// auto-reload. See https://vite.dev/guide/build.html#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  Sentry.captureException(event.payload, {
    tags: { 'rm.vite_preload_error': true },
  });
});

function main() {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const rootElement = document.getElementById('root')!;
  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    // TODO: Enable StrictMode when all components are compatible (checkout confirmation page is not)
    root.render(<App />);
  }
}

async function startDev() {
  if (import.meta.env.PUBLIC_DEVTOOLS) {
    const { enableMocking } = await import('../mocks/mocker');
    await enableMocking();
  }
  main();
}

if (IS_DEVELOPMENT) {
  void startDev();
} else {
  main();
}
